import { forwardRef } from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import { Stack, Box } from '@mui/material';
import { RiInformationLine } from '@remixicon/react';
import { RemixIcon } from '../Icon/RemixIcon';
import { Typography } from '../Typography/Typography';
import type { StructuredTooltipProps, TooltipProps } from './Tooltip.types';

const DefaultInfoIcon = () => (
    <RemixIcon icon={RiInformationLine} size="sm" color="inherit" />
);

type ContentProps = Pick<StructuredTooltipProps, 'title' | 'text' | 'badge'>;

const TooltipContent = ({ title, text, badge }: ContentProps) => (
    <Box sx={{ position: 'relative' }}>
        <Stack spacing={0.5} direction="column">
            <Stack spacing={0.5} direction="row" justifyContent="space-between">
                {title && (
                    <Typography
                        variant="xs"
                        fontWeight={text ? 'semibold' : 'regular'}
                        color="ds.fg.inverse.primary"
                        textAlign="left"
                    >
                        {title}
                    </Typography>
                )}

                {badge && (
                    <Typography
                        component="div"
                        variant="xs"
                        color="ds.fg.inverse.secondary"
                        sx={{
                            border: (theme) =>
                                `1px solid ${theme.palette.ds.border.inverse.primary}`,
                            borderRadius: 1,
                            px: 0.25,
                        }}
                    >
                        {badge}
                    </Typography>
                )}
            </Stack>
            {text && (
                <Typography
                    variant="xs"
                    fontWeight="regular"
                    color="ds.fg.inverse.secondary"
                    textAlign="left"
                >
                    {text}
                </Typography>
            )}
        </Stack>
    </Box>
);

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
    function Tooltip(
        {
            children,
            title,
            text,
            badge,
            rawContent,
            enabled = true,
            disableChildWrapper = false,
            open,
            onOpen,
            onClose,
            placement = 'top',
            arrow = true,
            ...rest
        },
        ref,
    ) {
        if (!enabled) {
            return children ?? null;
        }

        const childElement = children ?? <DefaultInfoIcon />;

        const wrappedChildren = disableChildWrapper ? (
            childElement
        ) : (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'inherit',
                }}
            >
                {childElement}
            </span>
        );

        return (
            <MuiTooltip
                ref={ref}
                title={
                    rawContent ?? (
                        <TooltipContent
                            title={title}
                            text={text}
                            badge={badge}
                        />
                    )
                }
                placement={placement}
                arrow={arrow}
                {...(open !== undefined && {
                    open,
                    onOpen,
                })}
                onClose={onClose}
                slotProps={{
                    tooltip: {
                        sx: {
                            py: 0.5,
                            px: 1.5,
                            backgroundColor: ({ palette }) =>
                                palette.ds.bg.inverse.primary,
                            '& .MuiTooltip-arrow': {
                                color: ({ palette }) =>
                                    palette.ds.bg.inverse.primary,
                            },
                        },
                    },
                }}
                {...rest}
            >
                {wrappedChildren}
            </MuiTooltip>
        );
    },
);
